import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Clock, XCircle } from 'lucide-react';
import api from '@/services/apiClient';
import useAuthStore from '@/store/authStore';
import Button from '@/components/ui/Button';
import Spinner from '@/components/ui/Spinner';
import styles from '../public.module.css';

/** Status da compra pós-checkout (polling até o webhook confirmar). */
export default function PurchaseStatus() {
  const { id } = useParams();
  const { t } = useTranslation();
  const { isAuthenticated } = useAuthStore();
  const [purchase, setPurchase] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    let alive = true;
    const load = () =>
      api
        .get(`/marketplace/purchases/${id}`)
        .then((res) => alive && setPurchase((res.data || res).purchase))
        .catch(() => alive && setFailed(true));
    load();
    const timer = setInterval(() => {
      // Para de consultar quando sai de pending.
      setPurchase((p) => {
        if (!p || p.status === 'pending') load();
        return p;
      });
    }, 5000);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [id, isAuthenticated]);

  const cancel = async () => {
    await api.delete(`/marketplace/purchases/${id}`).catch(() => {});
    const res = await api.get(`/marketplace/purchases/${id}`).catch(() => null);
    if (res) setPurchase((res.data || res).purchase);
  };

  let body;
  if (!isAuthenticated) {
    body = <p className={styles.hint}>{t('purchaseStatus.loginRequired')}</p>;
  } else if (failed) {
    body = <p className={styles.hint}>{t('purchaseStatus.notFound')}</p>;
  } else if (!purchase) {
    body = <div className={styles.center}><Spinner /></div>;
  } else if (purchase.status === 'paid') {
    body = (
      <div className={styles.status}>
        <CheckCircle2 size={44} color="var(--volt)" strokeWidth={2} />
        <div className={styles.statusTitle}>{t('purchaseStatus.paidTitle')}</div>
        <p className={styles.hint}>{t('purchaseStatus.paidDesc', { package: purchase.package?.title || '' })}</p>
      </div>
    );
  } else if (purchase.status === 'pending') {
    body = (
      <div className={styles.status}>
        <Clock size={44} color="var(--frost)" strokeWidth={2} />
        <div className={styles.statusTitle}>{t('purchaseStatus.pendingTitle')}</div>
        <p className={styles.hint}>{t('purchaseStatus.pendingDesc')}</p>
        {purchase.checkoutUrl && (
          <Button onClick={() => { window.location.href = purchase.checkoutUrl; }}>
            {t('purchaseStatus.openPayment')}
          </Button>
        )}
        <Button variant="ghost" onClick={cancel}>{t('purchaseStatus.cancel')}</Button>
      </div>
    );
  } else {
    body = (
      <div className={styles.status}>
        <XCircle size={44} color="var(--low)" strokeWidth={2} />
        <div className={styles.statusTitle}>{t(`purchaseStatus.${purchase.status}Title`)}</div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.brand}>move<span>z</span></div>
        {body}
      </div>
    </div>
  );
}
